import styled from 'styled-components';
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';

import { useDeepCompareEffect, useDeepCompareMemo } from '@clatter/platform';
import WarningIcon from '@mui/icons-material/Warning';
import MaterialTooltip from '@mui/material/Tooltip';

import { TemplateLabel } from './TemplateLabel';
import { DocumentOutputTable } from './DocumentOutputTable';
import { DocumentIcon } from '../../../UI/DocumentIcon/DocumentIcon';

interface Item {
  id: number;
  name: string;
  type: string;
  allowedOutputTypes: string[];
  lastUpdated: string;
  queryID: number;
  assetId?: number;
}

interface GenerateDocumentsSelectorProps {
  options: Item[];
  isQueryValid: boolean;
  isQueriesEnabled?: boolean;
  queriesResultsMap?: WorkspaceQueryInputResults | null;
  onChange: ({
    selectedItems,
    totalCount,
  }: {
    selectedItems: GenerateDocumentTemplateItem[];
    totalCount: number;
  }) => void;
}

const StyledWarningIcon = styled.div`
  svg {
    fill: red;
  }
`;

const hasQueryData = (item: Item, queriesResultsMap: WorkspaceQueryInputResults | null) =>
  queriesResultsMap?.[item.queryID]?.hasData;

const initializeSelectedItems = (options: Item[]) =>
  options.reduce((acc, item) => {
    acc[item.id] = item.allowedOutputTypes.reduce((typeAcc, type) => {
      typeAcc[type] = false;
      return typeAcc;
    }, {} as { [key: string]: boolean });
    return acc;
  }, {} as { [key: number]: { [key: string]: boolean } });

const getAllTypes = (options: Item[]) =>
  Array.from(
    new Set(
      options.reduce((acc, item) => {
        return acc.concat(item.allowedOutputTypes);
      }, [] as string[]),
    ),
  ) || [];

const toggleAllItems = ({
  options,
  selectedItems,
  queriesResultsMap,
  isQueriesEnabled,
}: {
  options: Item[];
  selectedItems: { [key: number]: { [key: string]: boolean } };
  queriesResultsMap: WorkspaceQueryInputResults | null;
  isQueriesEnabled?: boolean;
}) => {
  const itemsWithData = (isQueriesEnabled ? options.filter((item) => hasQueryData(item, queriesResultsMap)) : options)
    .filter((item) => item.assetId);

  const allSelected = itemsWithData.every((item) =>
    Object.entries(selectedItems[item.id] || {})
      .filter(([type]) => item.allowedOutputTypes.includes(type))
      .every(([_, selected]) => selected),
  );

  return options.reduce((acc, item) => {
    const hasData = isQueriesEnabled ? hasQueryData(item, queriesResultsMap) : true;

    acc[item.id] = item.allowedOutputTypes.reduce((typeAcc, type) => {
      typeAcc[type] = hasData && !!item.assetId ? !allSelected : false;
      return typeAcc;
    }, {} as { [key: string]: boolean });
    return acc;
  }, {} as { [key: number]: { [key: string]: boolean } });
};

const toggleTypeForAll = ({
  options,
  selectedItems,
  queriesResultsMap,
  type,
  isQueriesEnabled,
}: {
  options: Item[];
  selectedItems: { [key: number]: { [key: string]: boolean } };
  queriesResultsMap: WorkspaceQueryInputResults | null;
  type: string;
  isQueriesEnabled?: boolean;
}) => {
  const eligibleItems = (isQueriesEnabled ? options.filter((item) => hasQueryData(item, queriesResultsMap)) : options)
    .filter((item) => item.allowedOutputTypes.includes(type) && item.assetId);

  const allSelected = eligibleItems.every((item) => selectedItems[item.id]?.[type]);

  return options.reduce((acc, item) => {
    const hasData = isQueriesEnabled ? hasQueryData(item, queriesResultsMap) : true;
    const supportsType = item.allowedOutputTypes.includes(type);
    acc[item.id] = {
      ...selectedItems[item.id],
      [type]: hasData && supportsType && !!item.assetId ? !allSelected : false,
    };
    return acc;
  }, {} as { [key: number]: { [key: string]: boolean } });
};

export const GenerateDocumentsSelector: FC<GenerateDocumentsSelectorProps> = ({
  queriesResultsMap = null,
  isQueryValid,
  isQueriesEnabled,
  options = [],
  onChange,
}) => {
  const selectAllRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});

  const [selectedItems, setSelectedItems] = useState<{ [key: number]: { [key: string]: boolean } }>({});

  const allTypes: string[] = useDeepCompareMemo(() => getAllTypes(options), [options]);

  const handleItemChange = (itemId: number, type: string) => {
    setSelectedItems((prev) => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        [type]: !prev[itemId]?.[type],
      },
    }));
  };

  const handleSelectAllChange = () => {
    setSelectedItems(toggleAllItems({ options, selectedItems, queriesResultsMap, isQueriesEnabled }));
  };

  const handleSelectByTypeChange = (type: string) => {
    setSelectedItems(toggleTypeForAll({ options, selectedItems, queriesResultsMap, type, isQueriesEnabled }));
  };

  const getDocumentDetails = useCallback(
    (item: any) => {
      const countSelectedDocuments = Object.values(selectedItems?.[item.id] || {}).filter(
        (value) => value === true,
      ).length;

      //@ts-ignore
      const entitiesInQuery = queriesResultsMap?.[item?.queryID]?.data || [];

      // this could return also entities ids so we can show it in detail popup for example...
      const documentsToBuild = countSelectedDocuments * entitiesInQuery?.length;
      const entitiesIds = entitiesInQuery.map((item: any) => item?.POLICY).filter(Boolean);

      return {
        tooltip: `Used Policy ids (${entitiesIds?.length}) ${entitiesIds.join(', ')}`,
        count: countSelectedDocuments > 0 ? documentsToBuild : 0,
      };
    },
    [queriesResultsMap, selectedItems],
  );

  // initialize all selections
  useEffect(() => {
    setSelectedItems(initializeSelectedItems(options));
  }, [options]);

  // update checkboxes and call onChange
  useEffect(() => {
    const allSelected = options.every((item) =>
      Object.values(selectedItems[item.id] || {}).every((selected) => selected),
    );
    const someSelected = options.some((item) =>
      Object.values(selectedItems[item.id] || {}).some((selected) => selected),
    );

    if (selectAllRefs.current['all']) {
      selectAllRefs.current['all']!.checked = allSelected;
      selectAllRefs.current['all']!.indeterminate = !allSelected && someSelected;
    }

    allTypes.forEach((type) => {
      const itemsSupportingType = options.filter((item) => item.allowedOutputTypes.includes(type));
      const typeAllSelected = itemsSupportingType.every((item) => selectedItems[item.id]?.[type]);
      const typeSomeSelected = itemsSupportingType.some((item) => selectedItems[item.id]?.[type]);

      const ref = selectAllRefs.current[type];
      if (ref) {
        ref.checked = typeAllSelected;
        ref.indeterminate = !typeAllSelected && typeSomeSelected;
      }
    });

    const mapped: GenerateDocumentTemplateItem[] = Object.entries(selectedItems)
      .map(([id, formats]) => ({
        id: Number(id),
        output_types: Object.entries(formats)
          .filter(([_, value]) => value)
          .map(([key]) => key),
      }))
      .filter((item) => item.output_types.length > 0);

    const totalCount = options.reduce((acc, item) => acc + getDocumentDetails(item)?.count, 0);
    onChange({ selectedItems: mapped, totalCount: totalCount });
  }, [selectedItems, options]);

  // unselect options if their query has no data after a change
  useDeepCompareEffect(() => {
    const updatedSelectedItems = options.reduce((acc, item) => {
      const hasData = hasQueryData(item, queriesResultsMap);

      acc[item.id] = item.allowedOutputTypes.reduce((typeAcc, type) => {
        typeAcc[type] = hasData ? selectedItems[item.id]?.[type] || false : false;
        return typeAcc;
      }, {} as { [key: string]: boolean });

      return acc;
    }, {} as { [key: number]: { [key: string]: boolean } });

    setSelectedItems(updatedSelectedItems);
  }, [options, queriesResultsMap]);

  return (
    <DocumentOutputTable>
      <thead>
        <tr>
          <th>
            <div className="select-all">
              <input
                ref={(el) => { selectAllRefs.current['all'] = el; }}
                type="checkbox"
                disabled={!isQueryValid}
                onChange={handleSelectAllChange}
              />
              <span>All Templates</span>
            </div>
          </th>

          {isQueriesEnabled && <th></th>}

          {isQueriesEnabled && <th>Count</th>}

          {allTypes.map((type) => (
            <th key={type}>
              <DocumentIcon documentType={type} />
              <input
                ref={(el) => { selectAllRefs.current[type] = el; }}
                type="checkbox"
                disabled={!isQueryValid}
                onChange={() => handleSelectByTypeChange(type)}
              />
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {options.map((item) => {
          const queryResultHasData = isQueriesEnabled ? queriesResultsMap?.[item?.queryID]?.hasData : true;
          const documentDetails = getDocumentDetails(item);

          const isUnconfigured = !item.assetId;

          return (
            <tr key={item.id}>
              <td>
                <TemplateLabel name={item.name} lastUpdated={item?.lastUpdated} />
              </td>

              {isQueriesEnabled && (
                <td>
                  {isQueryValid && !queryResultHasData && (
                    <StyledWarningIcon>
                      <WarningIcon />
                    </StyledWarningIcon>
                  )}
                </td>
              )}

              {isQueriesEnabled && (
                <td>
                  <MaterialTooltip title={documentDetails?.tooltip}>
                    <span>{documentDetails?.count}</span>
                  </MaterialTooltip>
                </td>
              )}

              {allTypes.map((type) => (
                <td key={type}>
                  <MaterialTooltip
                    title={isUnconfigured ? 'This template requires configuration before it can be used.' : ''}
                    disableHoverListener={!isUnconfigured}
                  >
                    <span>
                      <input
                        type="checkbox"
                        disabled={isUnconfigured || !item?.allowedOutputTypes.includes(type) || !queryResultHasData}
                        checked={selectedItems[item.id]?.[type] || false}
                        onChange={() => handleItemChange(item.id, type)}
                      />
                    </span>
                  </MaterialTooltip>
                </td>
              ))}
            </tr>
          );
        })}
      </tbody>
    </DocumentOutputTable>
  );
};
//endregion
